/**
 * The screen which writes a letter to many people.
 *
 * Five small things, all of them about not sending a letter somebody did not
 * mean to send:
 *
 * - The two ways of reaching people are one choice. The half which is not
 *   chosen is greyed out and its fields are left out of the form, so a letter
 *   switched over to typed addresses cannot quietly carry the players picked
 *   a minute ago - and switching back finds them still there.
 * - The form says how many people the letter will reach while it is being
 *   written, and how many of them asked not to be written to.
 * - Addresses typed in by hand are counted as they are typed, and one which
 *   is not an address is named straight away rather than after send.
 * - A prepared letter fills the subject and the text of every language in, and
 *   asks first where something has already been written.
 * - Send asks once more, with the number of letters in the question. The
 *   letter to oneself does not ask anything, and is never mistaken for send.
 *
 * And the list of sent letters keeps up with the ones still going out.
 *
 * Without this script the form still works: both halves are filled in, and
 * the endpoint reads only the one the choice names.
 */

import { askConfirmation } from "./modal";
import { setInactive } from "./reveal";
import { allowUnload } from "./unsaved";
import { query, queryAll } from "../core/dom";
import { getJson } from "../core/http";

/** The audience whose readers are picked by name. */
const NAMED = "PLAYERS";

/** How often the list asks after the letters still going out, in milliseconds. */
const WATCH_EVERY = 3000;

/** What an address looks like closely enough to count, the way the endpoint reads one. */
const ADDRESS = /^[^@\s<>(),;:"[\]]+@[^@\s<>(),;:"[\]]+\.[^@\s<>(),;:"[\]]+$/;

/** Return `template` with every `{name}` of `values` filled in. */
function fill(template: string, values: Readonly<Record<string, string | number>>): string {
    return template.replace(/\{(\w+)\}/g, (whole, name: string) =>
        name in values ? String(values[name]) : whole,
    );
}

/** The addresses typed into a box, once each, and the pieces which are not addresses. */
export interface ReadAddresses {
    readonly found: string[];
    readonly wrong: string[];
}

/** Return what was typed into `value`, read the way the endpoint reads it. */
export function readAddresses(value: string): ReadAddresses {
    const seen = new Map<string, string>();
    const wrong: string[] = [];

    for (const piece of value.split(/[\s,;]+/)) {
        const candidate = piece.trim().replace(/^<|>$/g, "").trim();
        if (!candidate) {
            continue;
        }

        if (!ADDRESS.test(candidate) || candidate.length > 255) {
            wrong.push(candidate);
            continue;
        }

        if (!seen.has(candidate.toLowerCase())) {
            seen.set(candidate.toLowerCase(), candidate);
        }
    }

    return { found: [...seen.values()], wrong };
}

/** Return which of the two ways of reaching people is chosen. */
function deliveryOf(form: HTMLElement): string {
    return query<HTMLInputElement>("[data-mail-delivery]:checked", form)?.value ?? "ACCOUNTS";
}

/** Grey out the half of the form which is not chosen, and bring the other one back. */
export function followDelivery(form: HTMLElement): void {
    const chosen = deliveryOf(form);

    for (const block of queryAll<HTMLElement>("[data-mail-block]", form)) {
        setInactive(block, block.dataset["mailBlock"] !== chosen, "disable");
    }

    for (const choice of queryAll<HTMLInputElement>("[data-mail-delivery]", form)) {
        choice.closest(".mode-choice__item")?.classList.toggle("is-active", choice.checked);
    }

    // Bringing the accounts back switched every one of their fields on,
    // the picker of names included. The picker answers to the audience, so
    // the audience is asked to say again what it wants.
    if (chosen === "ACCOUNTS") {
        query<HTMLSelectElement>("[data-mail-audience]", form)?.dispatchEvent(new Event("change", { bubbles: true }));
    }
}

/** Say under the accounts how many people the chosen audience reaches. */
export function followAudience(form: HTMLElement): void {
    const audience = query<HTMLSelectElement>("[data-mail-audience]", form);
    const line = query<HTMLElement>("[data-mail-reach-text]", form);

    if (!audience || !line) {
        return;
    }

    const option = audience.selectedOptions[0];

    if (audience.value === NAMED) {
        line.textContent = line.dataset["textPlayers"] ?? "";

        return;
    }

    const reach = option?.dataset["mailReach"] ?? "0";
    const left = Number(option?.dataset["mailLeftOut"] ?? "0");

    line.textContent =
        left > 0
            ? fill(line.dataset["textConsent"] ?? "", { count: reach, left })
            : fill(line.dataset["textReach"] ?? "", { count: reach });
}

/** Count the addresses typed in so far, and name the first one which is not an address. */
export function followAddresses(form: HTMLElement): ReadAddresses {
    const box = query<HTMLTextAreaElement>("[data-mail-addresses]", form);
    const line = query<HTMLElement>("[data-mail-address-count]", form);
    const read = readAddresses(box?.value ?? "");

    if (!line) {
        return read;
    }

    const ceiling = Number(line.dataset["ceiling"] ?? "0");
    line.classList.toggle("is-invalid", read.wrong.length > 0 || (ceiling > 0 && read.found.length > ceiling));

    if (read.wrong.length > 0) {
        line.textContent = fill(line.dataset["textWrong"] ?? "", {
            count: read.found.length,
            address: read.wrong[0] ?? "",
        });
    } else if (ceiling > 0 && read.found.length > ceiling) {
        line.textContent = line.dataset["textTooMany"] ?? "";
    } else if (read.found.length === 0) {
        line.textContent = line.dataset["textNone"] ?? "";
    } else {
        line.textContent = fill(line.dataset["textCount"] ?? "", { count: read.found.length });
    }

    return read;
}

/** Return how many letters pressing send would post, as far as the page can tell. */
export function letterCount(form: HTMLElement): number {
    if (deliveryOf(form) === "ADDRESSES") {
        return readAddresses(query<HTMLTextAreaElement>("[data-mail-addresses]", form)?.value ?? "").found.length;
    }

    const audience = query<HTMLSelectElement>("[data-mail-audience]", form);

    if (audience?.value === NAMED) {
        return queryAll<HTMLInputElement>("[data-recipient-list] input[name=recipients]", form).length;
    }

    return Number(audience?.selectedOptions[0]?.dataset["mailReach"] ?? "0");
}

/** Return the question send asks, with the number of letters in it. */
export function sendQuestion(form: HTMLElement, button: HTMLElement): string {
    const count = letterCount(form);

    if (deliveryOf(form) === "ADDRESSES") {
        return fill(button.dataset["mailConfirmAddresses"] ?? "", { count });
    }

    const audience = query<HTMLSelectElement>("[data-mail-audience]", form);
    const template =
        audience?.value === NAMED ? button.dataset["mailConfirmPlayers"] : button.dataset["mailConfirmAccounts"];

    return fill(template ?? "", { count });
}

/** The subject and the text of a prepared letter in one language. */
interface TemplateWording {
    readonly subject: string;
    readonly body: string;
}

/** Return the wording a template button carries, by language. */
export function templateWording(button: HTMLElement): Record<string, TemplateWording> {
    try {
        const parsed: unknown = JSON.parse(button.dataset["mailTemplateWording"] ?? "{}");

        return parsed && typeof parsed === "object" ? (parsed as Record<string, TemplateWording>) : {};
    } catch {
        return {};
    }
}

/**
 * Fill the subject and the text of every language in with one prepared letter.
 *
 * Every language at once: a draft filled into one language alone would send
 * everybody else the language of the portal, which is the one thing writing
 * the letter in several languages is there to avoid.
 */
export async function useTemplate(form: HTMLElement, button: HTMLElement): Promise<void> {
    const wording = templateWording(button);
    const field = query<HTMLInputElement>("[data-mail-template-field]", form);
    const grid = button.closest<HTMLElement>(".mail-templates__grid");

    const pairs = Object.entries(wording)
        .map(([code, next]) => ({
            next,
            subject: query<HTMLInputElement>(`[name="subject_${code}"]`, form),
            body: query<HTMLTextAreaElement>(`[name="body_${code}"]`, form),
        }))
        .filter((pair) => pair.subject !== null && pair.body !== null);

    if (pairs.length === 0) {
        return;
    }

    const written = pairs.some(
        (pair) =>
            (pair.subject?.value.trim() !== "" || pair.body?.value.trim() !== "") &&
            (pair.subject?.value !== pair.next.subject || pair.body?.value !== pair.next.body),
    );

    if (written) {
        const label = grid?.dataset["textReplaceLabel"];
        const agreed = await askConfirmation(grid?.dataset["textReplace"] ?? "", label ? { confirm: label } : {});

        if (!agreed) {
            return;
        }
    }

    for (const pair of pairs) {
        if (!pair.subject || !pair.body) {
            continue;
        }

        pair.subject.value = pair.next.subject;
        pair.body.value = pair.next.body;

        // The badges of the languages, the counter under the editor, the guard
        // against leaving unsaved and the preview all listen for typing;
        // filling a field in is not typing.
        for (const changed of [pair.subject, pair.body]) {
            changed.dispatchEvent(new Event("input", { bubbles: true }));
            changed.dispatchEvent(new Event("change", { bubbles: true }));
        }
    }

    if (field) {
        field.value = button.dataset["mailTemplate"] ?? "";
    }

    for (const other of queryAll<HTMLElement>("[data-mail-template]", form)) {
        other.classList.toggle("is-active", other === button);
    }
}

/** Wire the form which writes a letter. */
export function bindComposer(form: HTMLFormElement): void {
    for (const choice of queryAll<HTMLInputElement>("[data-mail-delivery]", form)) {
        choice.addEventListener("change", () => followDelivery(form));
    }

    query<HTMLSelectElement>("[data-mail-audience]", form)?.addEventListener("change", () => followAudience(form));
    query<HTMLTextAreaElement>("[data-mail-addresses]", form)?.addEventListener("input", () => followAddresses(form));

    for (const button of queryAll<HTMLElement>("[data-mail-template]", form)) {
        button.addEventListener("click", (event: Event) => {
            event.preventDefault();
            void useTemplate(form, button);
        });
    }

    const send = query<HTMLButtonElement>("[data-mail-send]", form);
    let agreed = false;

    send?.addEventListener("click", (event: MouseEvent) => {
        if (agreed) {
            return;
        }

        event.preventDefault();

        // The browser's own complaints first: asking whether to send a letter
        // with no subject is asking the wrong question.
        if (!form.checkValidity()) {
            form.reportValidity();

            return;
        }

        const label = send.dataset["mailConfirmLabel"];

        void askConfirmation(sendQuestion(form, send), label ? { confirm: label, danger: true } : { danger: true }).then(
            (yes) => {
                if (!yes) {
                    return;
                }

                agreed = true;
                allowUnload();
                // Through the button, so the form goes where the button sends it.
                form.requestSubmit(send);
            },
        );
    });

    followDelivery(form);
    followAudience(form);
    followAddresses(form);
}

/** How one campaign stands, as the endpoint says it. */
interface CampaignProgress {
    readonly id: number;
    readonly state: string;
    readonly state_label: string;
    readonly sending: boolean;
    readonly total: number;
    readonly sent: number;
    readonly failed: number;
    readonly progress: number;
}

/** The classes of the badge for each state of a campaign. */
const STATE_BADGES: Readonly<Record<string, string>> = {
    SENT: "badge--ok",
    FAILED: "badge--danger",
    PARTIAL: "badge--warn",
    SENDING: "badge--accent",
};

/** Draw one row of the list the way the campaign stands now. */
export function drawProgress(table: HTMLElement, campaign: CampaignProgress): void {
    const row = query<HTMLElement>(`[data-mail-campaign="${campaign.id}"]`, table);
    if (!row) {
        return;
    }

    const badge = query<HTMLElement>("[data-mail-state]", row);
    if (badge) {
        badge.textContent = campaign.state_label;
        for (const name of Object.values(STATE_BADGES)) {
            badge.classList.remove(name);
        }
        badge.classList.add(STATE_BADGES[campaign.state] ?? "badge--muted");
    }

    const count = query<HTMLElement>("[data-mail-count]", row);
    if (count) {
        count.textContent = `${campaign.sent} / ${campaign.total}`;
    }

    const bar = query<HTMLElement>("[data-mail-bar]", row);
    if (bar) {
        bar.style.width = `${campaign.progress}%`;
    }

    const detail = query<HTMLElement>("[data-mail-detail]", row);
    if (detail && campaign.failed > 0) {
        detail.textContent = fill(table.dataset["textFailed"] ?? "", { count: campaign.failed });
    }
}

/** Keep the list of sent letters up with the ones still going out, for as long as any are. */
export function watchCampaigns(): void {
    const table = query<HTMLElement>("[data-mail-watch]");
    if (!table) {
        return;
    }

    let watching = (table.dataset["mailWatch"] ?? "").split(",").filter((id) => /^\d+$/.test(id));

    const look = async (): Promise<void> => {
        if (watching.length === 0) {
            return;
        }

        try {
            const answer = await getJson<{ campaigns: CampaignProgress[] }>(
                `/admin/mail/progress?ids=${watching.join(",")}`,
            );

            for (const campaign of answer.campaigns) {
                drawProgress(table, campaign);
            }

            const still = new Set(answer.campaigns.filter((campaign) => campaign.sending).map((c) => String(c.id)));
            watching = watching.filter((id) => still.has(id));
        } catch {
            // A missed look is caught up by the next one.
        }

        if (watching.length > 0) {
            window.setTimeout(() => void look(), WATCH_EVERY);
        }
    };

    if (watching.length > 0) {
        window.setTimeout(() => void look(), WATCH_EVERY);
    }
}

/**
 * Make the frame a letter is shown in as tall as the letter.
 *
 * The frame is of the portal's own origin and runs nothing, so its page can
 * be measured from here. A frame of a fixed height would either cut the
 * letter off or leave a band of nothing under a short one.
 */
export function fitFrame(frame: HTMLIFrameElement): void {
    const page = frame.contentDocument?.documentElement;
    if (!page) {
        return;
    }

    frame.style.height = `${Math.max(page.scrollHeight, 200)}px`;
}

/** Wire the screen which writes a letter, wherever it is on the page. */
export function initMailComposer(): void {
    for (const form of queryAll<HTMLFormElement>("form[data-mail-compose]")) {
        bindComposer(form);
    }

    // A frame arrives with the page or later, with every preview drawn. A load
    // does not bubble, so it is caught on its way down instead.
    document.addEventListener(
        "load",
        (event: Event) => {
            const target = event.target;
            if (target instanceof HTMLIFrameElement && target.matches("[data-mail-frame]")) {
                fitFrame(target);
            }
        },
        true,
    );

    for (const frame of queryAll<HTMLIFrameElement>("[data-mail-frame]")) {
        fitFrame(frame);
    }

    watchCampaigns();
}
